/**
 * Localized country-name helper — the ONE place the vitrine PWA turns an ISO country code into a
 * display name in the client's interface language. Reused by every country-showing screen
 * (CardIssuanceView, VitrineTopUpView, …) — do NOT re-inline Intl.DisplayNames elsewhere.
 *
 * The system's canonical country code is ISO alpha-3 (Card.providerId flattening, customer.legalCountry,
 * core CountryReferenceService.isKnownAlpha3, VitrineTopUpView). But Intl.DisplayNames({type:'region'}) only accepts
 * alpha-2 / M49 region codes — so we map alpha-3 → alpha-2 using the already-loaded Country[] (the single
 * country source, api/countries.ts) and then resolve the name. A legacy alpha-2 value is accepted too.
 *
 * Fallback chain (never throws, never a blank when a code is given): localized Intl name →
 * backend Country.name → the raw code.
 */
import { computed, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Country } from '@/api/countries';

// Intl.DisplayNames is not free to construct; cache one instance per locale.
const displayNamesCache = new Map<string, Intl.DisplayNames | null>();

function regionNames(locale: string): Intl.DisplayNames | null {
  if (!displayNamesCache.has(locale)) {
    try {
      displayNamesCache.set(locale, new Intl.DisplayNames([locale], { type: 'region' }));
    } catch {
      displayNamesCache.set(locale, null);
    }
  }
  return displayNamesCache.get(locale) ?? null;
}

/**
 * Localized country name for a code that may be alpha-3 (canonical) OR alpha-2 (legacy). Maps to alpha-2
 * via the loaded country list, resolves through Intl.DisplayNames for `locale`, and falls back to the
 * backend name, then the raw code. Returns '' only for an empty/blank code.
 */
export function countryLabel(code: string | null | undefined, countries: Country[], locale: string): string {
  const raw = (code ?? '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();

  const match = countries.find(c => c.alpha3.toUpperCase() === upper || c.alpha2.toUpperCase() === upper);
  // alpha-2 either from the matched record, or the raw code itself if it already looks like alpha-2.
  const alpha2 = match?.alpha2?.toUpperCase() ?? (upper.length === 2 ? upper : undefined);

  if (alpha2) {
    const dn = regionNames(locale);
    if (dn) {
      try {
        const name = dn.of(alpha2);
        // Intl echoes the input back when it can't resolve — treat that as "no name".
        if (name && name.toUpperCase() !== alpha2) return name;
      } catch { /* fall through to the backend name / raw code */ }
    }
  }
  return match?.name || raw;
}

/**
 * Composable form: bind to a reactive Country[] once, get a `(code) => localizedName` function that tracks
 * both the loaded list and the current i18n locale. Use in views that render country labels in templates.
 */
export function useCountryLabel(countriesRef: Ref<Country[]>): (code: string | null | undefined) => string {
  const { locale } = useI18n();
  const list = computed(() => countriesRef.value);
  return (code) => countryLabel(code, list.value, locale.value);
}
